export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  userId: number;
  email: string;
  company: string;
  accessLevel: string;
  lastLogin: string;
  username: string;
  temppassword: boolean;
  passwordExpired?: boolean;
  barksAdmin?: boolean;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
}

export interface UserInfo {
  userId: number;
  email: string;
  accessLevel: string;
  company: string;
  lastLogin: string;
  username: string;
  temppassword: boolean;
  passwordExpired?: boolean;
  barksAdmin?: boolean;
}

export default class StorageService {
  private static readonly ACCESS_TOKEN_KEY = 'access_token';
  private static readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private static readonly TOKEN_EXPIRY_KEY = 'token_expiry';
  private static readonly USER_INFO_KEY = 'user_info';
  private static readonly MODULE_PERMISSION_KEY = 'module_permissions';
  private static readonly DETAILED_PERMISSION_KEY = 'detailed_permissions';
  private static readonly RECENT_SEARCH_PART_NUMBER_KEY = 'recent_search_part_number';
  private static readonly FORCE_TEMP_PASSWORD = 'force_temp_password';
  private static readonly RECENT_PASSWORD_CHANGE = 'recent_password_change';
  private static readonly OENTRY_ADMIN_CUSTOMER_KEY = 'oentry_admin_customer';
  private static readonly OENTRY_HEADER_KEY = 'oentry_header';
  private static readonly OENTRY_HEADER_VALUE_KEY = 'oentry_header_value';
  private static readonly OENTRY_HEADER_CART_KEY = 'oentry_header_cart_value';
  private static readonly OENTRY_HEADER_SET_POINT_KEY = 'oentry_header_set_point_value';
  private static readonly OENTRY_HEADER_CART_ID_KEY = 'oentry_header_cart_id_value';
  private static readonly OENTRY_HEADER_CART_AMOUNT_KEY = 'oentry_header_cart_amount_value';
  private static readonly SELECTED_EXTRANET_KEY = 'selected_extranet';
  private static readonly PREVIOUS_EXTRANET_KEY = 'previous_extranet';

  /**
   * Store tokens and user info in localStorage
   */
  static setTokens(tokens: TokenResponse): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refreshToken);

    const accessExpiry = this.extractExpiry(tokens.accessToken);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, accessExpiry.toString());

    const userInfo: UserInfo = {
      userId: tokens.userId,
      email: tokens.email,
      accessLevel: tokens.accessLevel,
      company: tokens.company,
      lastLogin: tokens.lastLogin,
      username: tokens.username,
      temppassword: tokens.temppassword || false,
      passwordExpired: tokens.passwordExpired || false,
      barksAdmin: tokens.barksAdmin || false,
    };
    localStorage.setItem(this.USER_INFO_KEY, JSON.stringify(userInfo));
  }

  static saveTokens(tokens: RefreshTokenResponse): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refreshToken);

    const accessExpiry = this.extractExpiry(tokens.accessToken);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, accessExpiry.toString());
  }

  /**
   * Retrieve the stored access token
   */
  static getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Retrieve the stored refresh token
   */
  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Retrieve stored user info
   */
  static getUserInfo(): UserInfo | null {
    const data = localStorage.getItem(this.USER_INFO_KEY);
    return data ? (JSON.parse(data) as UserInfo) : null;
  }

  static getModulePermissions(): any {
    const data = localStorage.getItem(this.MODULE_PERMISSION_KEY);
    return data ? JSON.parse(data) : null;
  }

  static getDetailedPermissions(): any {
    const data = localStorage.getItem(this.DETAILED_PERMISSION_KEY);
    return data ? JSON.parse(data) : null;
  }

  static setModulePermissions(permissions: any): void {
    localStorage.setItem(this.MODULE_PERMISSION_KEY, JSON.stringify(permissions));
  }

  static setDetailedPermissions(permissions: any): void {
    localStorage.setItem(this.DETAILED_PERMISSION_KEY, JSON.stringify(permissions));
  }

  static setRecentSearchPartNumber(partNo: string): void {
    localStorage.setItem(this.RECENT_SEARCH_PART_NUMBER_KEY, partNo);
  }

  static getRecentSearchPartNumber(): string {
    return localStorage.getItem(this.RECENT_SEARCH_PART_NUMBER_KEY) || '';
  }

  static setForceTempPassword(status: string): void {
    localStorage.setItem(this.FORCE_TEMP_PASSWORD, status);
  }

  static getForceTempPassword(): string {
    return localStorage.getItem(this.FORCE_TEMP_PASSWORD) || '';
  }

  static setRecentPasswordChange(status: string): void {
    localStorage.setItem(this.RECENT_PASSWORD_CHANGE, status);
  }

  static getRecentPasswordChange(): string {
    return localStorage.getItem(this.RECENT_PASSWORD_CHANGE) || '';
  }

  static clearRecentPasswordChange(): void {
    localStorage.removeItem(this.RECENT_PASSWORD_CHANGE);
  }

  static setOEntryHeader(payload: any): void {
    localStorage.setItem(this.OENTRY_HEADER_KEY, JSON.stringify(payload));
  }

  static getOEntryHeader(): any {
    const data = localStorage.getItem(this.OENTRY_HEADER_KEY);
    return data ? JSON.parse(data) : null;
  }

  static setOEntryHeaderValue(payload: any): void {
    localStorage.setItem(this.OENTRY_HEADER_VALUE_KEY, JSON.stringify(payload));
  }

  static getOEntryHeaderValue(): any {
    const data = localStorage.getItem(this.OENTRY_HEADER_VALUE_KEY);
    return data ? JSON.parse(data) : null;
  }

  static setOEntryAdminCustomer(customerId: string): void {
    localStorage.setItem(this.OENTRY_ADMIN_CUSTOMER_KEY, customerId);
  }

  static getOEntryAdminCustomer(): any {
    return localStorage.getItem(this.OENTRY_ADMIN_CUSTOMER_KEY) || '';
  }

  static setOEntryHeaderCartValue(payload: any): void {
    const existingCart = this.getOEntryHeaderCartValue() || [];
    if (Array.isArray(existingCart)) {
      payload.forEach((newItem: any) => {
        const index = existingCart.findIndex(
          (item) => item.partNumber === newItem.partNumber
        );

        if (index > -1) {
          // existingCart[index].quantity =
          //   Number(existingCart[index].quantity) + Number(newItem.quantity);
        } else {
          existingCart.push(newItem);
        }
      });
    }
    localStorage.setItem(this.OENTRY_HEADER_CART_KEY, JSON.stringify(existingCart));
  }

  static getOEntryHeaderCartValue(): any {
    const data = localStorage.getItem(this.OENTRY_HEADER_CART_KEY);
    return data ? JSON.parse(data) : null;
  }

  static setOEntryHeaderSetPointValue(payload: any): void {
    localStorage.setItem(this.OENTRY_HEADER_SET_POINT_KEY, JSON.stringify(payload));
  }

  static getOEntryHeaderSetPointValue(): any {
    const data = localStorage.getItem(this.OENTRY_HEADER_SET_POINT_KEY);
    return data ? JSON.parse(data) : null;
  }

  static removeOEntryHeaderSetPointValue(): any {
    localStorage.removeItem(this.OENTRY_HEADER_SET_POINT_KEY);
  }

  static setOEntryCartId(id: string): void {
    localStorage.setItem(this.OENTRY_HEADER_CART_ID_KEY, id);
  }

  static getOEntryCartId(): any {
    return localStorage.getItem(this.OENTRY_HEADER_CART_ID_KEY) || '';
  }

  static setOEntryCartTotalAmount(amount: string): void {
    localStorage.setItem(this.OENTRY_HEADER_CART_AMOUNT_KEY, amount);
  }

  static getOEntryCartTotalAmount(): any {
    return localStorage.getItem(this.OENTRY_HEADER_CART_AMOUNT_KEY) || '';
  }

  /**
   * Store the selected extranet for the current session
   */
  static setSelectedExtranet(extranetName: string): void {
    localStorage.setItem(this.SELECTED_EXTRANET_KEY, extranetName);
  }

  /**
   * Get the selected extranet for the current session
   */
  static getSelectedExtranet(): string | null {
    return localStorage.getItem(this.SELECTED_EXTRANET_KEY);
  }

  /**
   * Get the previous extranet selection
   */
  static getPreviousExtranet(): string | null {
    return localStorage.getItem(this.PREVIOUS_EXTRANET_KEY);
  }

  /**
   * Set the previous extranet selection
   */
  static setPreviousExtranet(extranetName: string): void {
    localStorage.setItem(this.PREVIOUS_EXTRANET_KEY, extranetName);
  }

  /**
   * Clear module permissions
   */
  static clearModulePermissions(): void {
    localStorage.removeItem(this.MODULE_PERMISSION_KEY);
  }

  /**
   * Clear detailed permissions
   */
  static clearDetailedPermissions(): void {
    localStorage.removeItem(this.DETAILED_PERMISSION_KEY);
  }

  /**
   * Clear all tokens and user info
   */
  static clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    localStorage.removeItem(this.USER_INFO_KEY);
    localStorage.removeItem(this.MODULE_PERMISSION_KEY);
    localStorage.removeItem(this.DETAILED_PERMISSION_KEY);
    localStorage.removeItem(this.RECENT_SEARCH_PART_NUMBER_KEY);
    localStorage.removeItem(this.SELECTED_EXTRANET_KEY);
    localStorage.removeItem(this.FORCE_TEMP_PASSWORD);
    localStorage.removeItem(this.RECENT_PASSWORD_CHANGE);
    localStorage.removeItem(this.OENTRY_HEADER_KEY);
    localStorage.removeItem(this.OENTRY_HEADER_VALUE_KEY);
    localStorage.removeItem(this.OENTRY_ADMIN_CUSTOMER_KEY);
    localStorage.removeItem(this.OENTRY_HEADER_CART_KEY);
    localStorage.removeItem(this.OENTRY_HEADER_SET_POINT_KEY);
    localStorage.removeItem(this.OENTRY_HEADER_CART_ID_KEY);
    localStorage.removeItem(this.OENTRY_HEADER_CART_AMOUNT_KEY);
    localStorage.removeItem(this.SELECTED_EXTRANET_KEY);
    localStorage.removeItem(this.PREVIOUS_EXTRANET_KEY);
    localStorage.clear();
  }

  /**
   * Clear only authentication data while preserving order entry data
   * This prevents order header data from being lost during logout
   */
  static clearAuthOnly(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    localStorage.removeItem(this.USER_INFO_KEY);
    localStorage.removeItem(this.MODULE_PERMISSION_KEY);
    localStorage.removeItem(this.DETAILED_PERMISSION_KEY);
    localStorage.removeItem(this.RECENT_SEARCH_PART_NUMBER_KEY);
    localStorage.removeItem(this.SELECTED_EXTRANET_KEY);
    localStorage.removeItem(this.FORCE_TEMP_PASSWORD);
    localStorage.removeItem(this.RECENT_PASSWORD_CHANGE);
    localStorage.removeItem(this.PREVIOUS_EXTRANET_KEY);
    // Note: Order entry data is preserved for session continuity
  }

  static clearCart(): void {
    localStorage.removeItem(this.OENTRY_HEADER_KEY);
    localStorage.removeItem(this.OENTRY_HEADER_VALUE_KEY);
    localStorage.removeItem(this.OENTRY_ADMIN_CUSTOMER_KEY);
    localStorage.removeItem(this.OENTRY_HEADER_CART_KEY);
    localStorage.removeItem(this.OENTRY_HEADER_SET_POINT_KEY);
    localStorage.removeItem(this.OENTRY_HEADER_CART_ID_KEY);
    localStorage.removeItem(this.OENTRY_HEADER_CART_AMOUNT_KEY);
    localStorage.removeItem(this.SELECTED_EXTRANET_KEY);
    localStorage.removeItem(this.PREVIOUS_EXTRANET_KEY);
  }

  /**
   * Check if the access token is expired (with 30s buffer)
   */
  static isTokenExpired(): boolean {
    const expiryStr = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiryStr) return true;
    const expiry = parseInt(expiryStr, 10);
    return Date.now() >= expiry - 30000;
  }

  /**
   * Check if the access token will expire within the specified time window
   */
  static willTokenExpireSoon(timeWindowMs: number): boolean {
    const expiryStr = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (!expiryStr) return true;
    const expiry = parseInt(expiryStr, 10);
    return Date.now() >= expiry - timeWindowMs;
  }

  /**
   * Decode JWT token and extract expiry in ms
   */
  private static extractExpiry(token: string): number {
    try {
      const [, payload] = token.split('.');
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const json = atob(base64);
      const { exp } = JSON.parse(json);
      return exp * 1000;
    } catch (err) {
      return 0;
    }
  }
}
